import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, Users, MoreHorizontal, CheckCircle2, Clock, XCircle, Copy, Link2, Loader2, MessageSquareText, PackageCheck, TrendingUp } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  approved: { icon: CheckCircle2, cls: 'bg-[#7dd4c5]/15 text-[#7dd4c5] border-[#7dd4c5]/30', label: 'Approved' },
  pending: { icon: Clock, cls: 'bg-[#ea9a6f]/15 text-[#ea9a6f] border-[#ea9a6f]/30', label: 'Pending' },
  draft: { icon: Clock, cls: 'bg-[#8a877f]/15 text-[#8a877f] border-[#8a877f]/30', label: 'Draft' },
  rejected: { icon: XCircle, cls: 'bg-red-500/15 text-red-400 border-red-500/30', label: 'Rejected' }
};

export default function Team() {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [inviteSite, setInviteSite] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [generatedToken, setGeneratedToken] = useState('');
  const [creatingLink, setCreatingLink] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [exportingBatch, setExportingBatch] = useState(false);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/billing/usage`).then((res) => setUsage(res.data)).catch(() => {});
  }, [user]);

  const loadHeadshots = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/headshots`);
      setRows(res.data.map((h) => ({
        id: h.id, name: h.worker_name, role: h.worker_role, site: h.site,
        status: h.status, created: new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        img: `${process.env.REACT_APP_BACKEND_URL}${h.image_url}`,
      })));
    } catch {
      toast({ title: 'Could not load team roster' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHeadshots(); }, [user]);

  const filtered = rows.filter((r) => (
    (filter === 'all' || r.status === filter) &&
    (siteFilter === 'all' || r.site === siteFilter) &&
    (r.name.toLowerCase().includes(query.toLowerCase()) || r.role.toLowerCase().includes(query.toLowerCase()))
  ));

  const sites = Array.from(new Set(rows.map((r) => r.site).filter(Boolean))).sort();

  const counts = {
    total: rows.length,
    approved: rows.filter((r) => r.status === 'approved').length,
    pending: rows.filter((r) => r.status === 'pending').length,
    rejected: rows.filter((r) => r.status === 'rejected').length
  };

  const refreshUsage = () => {
    axios.get(`${API}/billing/usage`).then((res) => setUsage(res.data)).catch(() => {});
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/headshots/${id}/status`, { status });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast({ title: `Marked as ${status}` });
      refreshUsage();
    } catch (err) {
      toast({ title: 'Update failed', description: err.response?.data?.detail });
    }
  };

  const createInvite = async () => {
    if (!user) return;
    setCreatingLink(true);
    try {
      const res = await axios.post(`${API}/magic-links`, {
        worker_name: inviteName, worker_role: inviteRole, site: inviteSite,
      });
      setGeneratedToken(res.data.token);
      setGeneratedLink(`${window.location.origin}/capture/${res.data.token}`);
    } catch {
      toast({ title: 'Could not create invite link' });
    } finally {
      setCreatingLink(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    toast({ title: 'Link copied', description: 'Send it to your crew member.' });
  };

  const sendSms = async () => {
    if (!phoneNumber) return;
    setSendingSms(true);
    try {
      await axios.post(`${API}/magic-links/${generatedToken}/send-sms`, {
        phone_number: phoneNumber, capture_url: generatedLink,
      });
      toast({ title: 'Text sent', description: `Invite link texted to ${phoneNumber}.` });
      setPhoneNumber('');
    } catch (err) {
      toast({ title: 'Could not send text', description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setSendingSms(false);
    }
  };

  const closeInvite = () => {
    setInviteOpen(false);
    setInviteName(''); setInviteRole(''); setInviteSite('');
    setGeneratedLink(''); setGeneratedToken(''); setPhoneNumber('');
    loadHeadshots();
  };

  const downloadAllBadges = async () => {
    setExportingBatch(true);
    try {
      const res = await axios.get(`${API}/headshots/batch-badge-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'crewface_badges.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Nothing to export', description: err.response?.data?.detail || 'Approve at least one headshot first.' });
    } finally {
      setExportingBatch(false);
    }
  };

  if (authLoading) {
    return <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#ea9a6f] animate-spin" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0c]">
        <Navbar />
        <div className="max-w-md mx-auto text-center py-32 px-5">
          <h2 className="serif text-3xl text-white mb-3">Sign in to view your team</h2>
          <Link to="/sign-in"><Button className="bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] rounded-full px-6 h-11">Sign in</Button></Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8 md:pt-12 pb-16">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <div className="mono text-xs text-[#7a7770] uppercase tracking-wider mb-1">Admin</div>
            <h1 className="serif text-4xl md:text-5xl text-white">Team dashboard</h1>
            <p className="text-[#8a877f] mt-2">Review, approve, and export your crew's headshots.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={downloadAllBadges} disabled={exportingBatch} variant="outline" data-testid="download-all-badges-button" className="border-[#2a2a30] bg-transparent text-white hover:bg-[#14141a] rounded-full">
              {exportingBatch ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-1.5" />} Download all badges
            </Button>
            <Button onClick={() => setInviteOpen(true)} data-testid="invite-crew-button" className="bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] rounded-full"><Link2 className="w-4 h-4 mr-1.5" /> Invite crew</Button>
          </div>
        </div>

        {/* Plan usage banner */}
        {usage && (
          <div data-testid="usage-banner" className={`flex flex-wrap items-center gap-3 justify-between rounded-2xl border px-5 py-3.5 mb-8 ${(usage.headshots_used >= usage.headshots_limit || usage.seats_used >= usage.seats_limit) ? 'border-[#ea9a6f]/40 bg-[#1a1410]' : 'border-[#1e1e26] bg-[#101014]'}`}>
            <div className="flex items-center gap-2 text-sm text-[#c9c6bf]">
              <TrendingUp className="w-4 h-4 text-[#ea9a6f]" />
              <span className="text-white font-medium">{usage.plan_label} plan</span>
              <span className="text-[#5a5850]">·</span>
              <span data-testid="usage-seats">{usage.seats_used}/{usage.seats_limit} seats</span>
              <span className="text-[#5a5850]">·</span>
              <span data-testid="usage-headshots">{usage.headshots_used}/{usage.headshots_limit} headshots {usage.plan === 'free' ? '(trial)' : 'this month'}</span>
            </div>
            <Link to="/pricing" data-testid="usage-upgrade-link" className="text-sm text-[#ea9a6f] hover:underline font-medium">Upgrade plan →</Link>
          </div>
        )}

        {/* KPI */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Total crew', value: counts.total, icon: Users, color: '#e8e6e1' },
            { label: 'Approved', value: counts.approved, icon: CheckCircle2, color: '#7dd4c5' },
            { label: 'Pending review', value: counts.pending, icon: Clock, color: '#ea9a6f' },
            { label: 'Rejected', value: counts.rejected, icon: XCircle, color: '#f87171' }
          ].map((k) => (
            <div key={k.label} className="bg-[#101014] border border-[#1e1e26] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-wider text-[#7a7770]">{k.label}</div>
                <k.icon className="w-4 h-4" style={{ color: k.color }} />
              </div>
              <div className="serif text-4xl text-white">{k.value}</div>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap gap-3 mb-4 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-[#7a7770] absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or role…" data-testid="team-search-input" className="w-full bg-[#101014] border border-[#1e1e26] rounded-full h-10 pl-9 pr-4 text-white text-sm outline-none focus:border-[#ea9a6f]" />
          </div>
          <div className="flex gap-1.5 bg-[#101014] border border-[#1e1e26] rounded-full p-1">
            {['all', 'approved', 'pending', 'rejected'].map((s) => (
              <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`} className={`text-xs px-3.5 py-1.5 rounded-full capitalize transition-colors ${filter === s ? 'bg-[#ea9a6f] text-[#0a0a0c]' : 'text-[#c9c6bf] hover:text-white'}`}>{s}</button>
            ))}
          </div>
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger data-testid="site-filter-select" className="bg-[#101014] border-[#1e1e26] text-white rounded-full h-10 w-[180px]">
              <Filter className="w-4 h-4 mr-1.5 text-[#7a7770]" /><SelectValue placeholder="Site" />
            </SelectTrigger>
            <SelectContent className="bg-[#101014] border-[#26262e] text-white">
              <SelectItem value="all" className="focus:bg-[#1a1a20] focus:text-white">All sites</SelectItem>
              {sites.map((s) => <SelectItem key={s} value={s} className="focus:bg-[#1a1a20] focus:text-white">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Roster table */}
        <div className="bg-[#101014] border border-[#1e1e26] rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#1e1e26] text-xs uppercase tracking-wider text-[#7a7770]">
            <div className="col-span-5 md:col-span-4">Worker</div>
            <div className="hidden md:block md:col-span-3">Site</div>
            <div className="col-span-3 md:col-span-2">Status</div>
            <div className="col-span-3 md:col-span-2">Created</div>
            <div className="col-span-1 text-right">·</div>
          </div>
          {loading && <div className="px-5 py-10 text-center text-[#7a7770] text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading roster…</div>}
          {!loading && filtered.map((r) => {
            const s = STATUS_STYLES[r.status] || STATUS_STYLES.draft;
            return (
              <div key={r.id} data-testid="roster-row" className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#1a1a20] items-center hover:bg-[#14141a] transition-colors">
                <div className="col-span-5 md:col-span-4 flex items-center gap-3">
                  <img src={r.img} alt={r.name} className="w-10 h-10 rounded-full object-cover border border-[#26262e]" />
                  <div>
                    <div className="text-white text-sm font-medium">{r.name}</div>
                    <div className="text-xs text-[#7a7770]">{r.role}</div>
                  </div>
                </div>
                <div className="hidden md:block md:col-span-3 text-sm text-[#c9c6bf]">{r.site}</div>
                <div className="col-span-3 md:col-span-2">
                  <Badge className={`${s.cls} border rounded-full font-normal`}><s.icon className="w-3 h-3 mr-1" />{s.label}</Badge>
                </div>
                <div className="col-span-3 md:col-span-2 mono text-xs text-[#8a877f]">{r.created}</div>
                <div className="col-span-1 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`row-menu-${r.id}`}><MoreHorizontal className="w-4 h-4 text-[#7a7770] ml-auto" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#101014] border-[#26262e] text-white">
                      <DropdownMenuItem data-testid={`approve-${r.id}`} onClick={() => setStatus(r.id, 'approved')} className="focus:bg-[#1a1a20] focus:text-white cursor-pointer">Approve</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`reject-${r.id}`} onClick={() => setStatus(r.id, 'rejected')} className="focus:bg-[#1a1a20] focus:text-white cursor-pointer">Reject</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`badge-pdf-${r.id}`} onClick={() => window.open(`${API}/headshots/${r.id}/badge-pdf`, '_blank')} className="focus:bg-[#1a1a20] focus:text-white cursor-pointer">Download badge PDF</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })}
          {!loading && filtered.length === 0 && <div className="px-5 py-10 text-center text-[#7a7770] text-sm">No workers match your filters. Invite a crew member to get started.</div>}
        </div>
      </div>
      <Footer />

      <Dialog open={inviteOpen} onOpenChange={(o) => { if (!o) closeInvite(); else setInviteOpen(true); }}>
        <DialogContent className="bg-[#101014] border-[#26262e] text-white">
          <DialogHeader>
            <DialogTitle className="serif text-2xl">Invite a crew member</DialogTitle>
          </DialogHeader>
          {!generatedLink ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#c9c6bf]">Worker name (optional)</label>
                <input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="e.g. Marcus J." data-testid="invite-name-input" className="mt-1 w-full bg-[#0a0a0c] border border-[#26262e] rounded-md h-10 px-3 text-white text-sm outline-none focus:border-[#ea9a6f]" />
              </div>
              <div>
                <label className="text-xs text-[#c9c6bf]">Role (optional)</label>
                <input value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} placeholder="e.g. Site Foreman" data-testid="invite-role-input" className="mt-1 w-full bg-[#0a0a0c] border border-[#26262e] rounded-md h-10 px-3 text-white text-sm outline-none focus:border-[#ea9a6f]" />
              </div>
              <div>
                <label className="text-xs text-[#c9c6bf]">Site (optional)</label>
                <input value={inviteSite} onChange={(e) => setInviteSite(e.target.value)} placeholder="e.g. Site A - Denver" data-testid="invite-site-input" className="mt-1 w-full bg-[#0a0a0c] border border-[#26262e] rounded-md h-10 px-3 text-white text-sm outline-none focus:border-[#ea9a6f]" />
              </div>
              <p className="text-xs text-[#7a7770]">Generates a zero-friction capture link. Copy it or text it straight to your worker's phone — no account required on their end.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-[#0a0a0c] border border-[#1e1e26] rounded-lg p-3 text-sm text-[#7dd4c5] break-all" data-testid="generated-magic-link">{generatedLink}</div>
              <Button onClick={copyLink} data-testid="copy-link-button" className="w-full bg-[#7dd4c5] hover:bg-[#6bc4b5] text-[#0a0a0c] rounded-full h-10"><Copy className="w-4 h-4 mr-2" /> Copy link</Button>
              <div className="dashed-divider" />
              <label className="text-xs text-[#c9c6bf]">Or text it to their phone</label>
              <div className="flex gap-2">
                <input value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="+14155551234" data-testid="invite-phone-input" className="flex-1 bg-[#0a0a0c] border border-[#26262e] rounded-md h-10 px-3 text-white text-sm outline-none focus:border-[#ea9a6f]" />
                <Button onClick={sendSms} disabled={sendingSms || !phoneNumber} data-testid="send-sms-button" className="bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] rounded-full px-4 shrink-0">
                  {sendingSms ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquareText className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-[10px] text-[#5a5850]">Use E.164 format, e.g. +14155551234</p>
            </div>
          )}
          <DialogFooter>
            {!generatedLink ? (
              <Button onClick={createInvite} disabled={creatingLink} data-testid="create-invite-submit" className="bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] rounded-full">
                {creatingLink ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : 'Generate link'}
              </Button>
            ) : (
              <Button onClick={closeInvite} data-testid="invite-done-button" variant="outline" className="border-[#2a2a30] bg-transparent text-white hover:bg-[#14141a] rounded-full">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
